import { ConfigProvider, Flex, Form, Typography } from 'antd';
import PageContainer from '../../common/components/PageContainer';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { primitiveColors, spacing } from '../../theming';
import TwoFactorAuth from '../../components/userSettings/twoFactorAuth';
import EmailPreferences from '../../components/userSettings/emailPreferences';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect } from 'react';
import ProfileForm from '../../components/userSettings/profileForm';
import { passwordResetStages } from '../../common/constants/passwordResetStages';
import { renderResetPasswordContent } from './helper';
import classes from './style.module.scss';
import { useAuthService } from '../../services/authService';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateToken } from '../../services/tokenService';
import { TOKEN, REFRESH_TOKEN } from '../../common/constants/localStorage';
import { notifications } from '../../common/constants/notifications';
import { useImpersonation } from '../../hooks/useImpersonation';
import { ReadOnlyNotice } from '../../components/impersonation/ReadOnlyNotice';

const { Title } = Typography;

const Section = PageContainer; // for better readability

const EMAIL_PREFERENCES_ID = 'email-preferences';

export default function UserSettings() {
    const mobileDevicesBreakpoint = useMediaQuery('only screen and (max-width : 840px)');
    const { t } = useTranslation([
        'settings',
        'form',
        'notification',
        'apiResponses',
        'resetPassword',
        'profile',
        'auth',
    ]);
    const { currentUser, fetchUser } = useAuth();
    const { verifyEmail, verifyOtp } = useAuthService();
    const location = useLocation();
    const navigate = useNavigate();
    // a read-only impersonation sees the settings but cannot change them
    const { isImpersonating } = useImpersonation();

    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [emailForm] = Form.useForm();
    const [otpForm] = Form.useForm();

    const [loading, setLoading] = useState({ profile: false, resetPassword: false, email: false, otp: false });
    const [resetPasswordStage, setResetPasswordStage] = useState(passwordResetStages.EMAIL);
    // taken on the first render: the email-link handling below rewrites the URL, which drops the hash
    const [initialHash] = useState(() => location.hash);

    // the "Manage email preferences" link in a marketing email lands on /user-settings#email-preferences
    useEffect(() => {
        if (initialHash === `#${EMAIL_PREFERENCES_ID}`) {
            document.getElementById(EMAIL_PREFERENCES_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }
    }, [initialHash]);

    // #region Handlers
    // Handle URL query params after email verification
    useEffect(() => {
        // these links finish a consumer's own email change; their tokens never replace an impersonation token
        if (isImpersonating) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const accessToken = searchParams.get('t');
        const refreshToken = searchParams.get('rt');
        const error = searchParams.get('error');
        // a 2FA consumer's link changes the email but signs nobody in
        const emailVerified = searchParams.get('emailVerified');

        const handleResponse = type => {
            globalNotification.request({
                type,
                resource: notifications.resource.USERS,
                action: notifications.action.UPDATE,
                singular: true,
            });
            // Clean up URL
            navigate('/user-settings', { replace: true });

            fetchUser();
        };

        if (accessToken && refreshToken) {
            updateToken(TOKEN, accessToken);
            updateToken(REFRESH_TOKEN, refreshToken);

            handleResponse(notificationType.SUCCESS);
        } else if (emailVerified) {
            handleResponse(notificationType.SUCCESS);
        } else if (error) {
            handleResponse(notificationType.ERROR);
        }
    }, [location.search, navigate, t, fetchUser, isImpersonating]);

    const handleEmailSubmit = async values => {
        setLoading(prev => ({ ...prev, email: true }));

        try {
            await verifyEmail(values.email);

            setResetPasswordStage(passwordResetStages.OTP);
        } catch (error) {
            const errCode = error?.response?.data?.code;
            globalNotification.open({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errCode}.message`),
                description: t(`apiResponses:${errCode}.description`),
            });
        } finally {
            setLoading(prev => ({ ...prev, email: false }));
        }
    };

    const handleOtpSubmit = async values => {
        setLoading(prev => ({ ...prev, otp: true }));

        try {
            await verifyOtp(values.otp);

            setResetPasswordStage(passwordResetStages.RESET);
        } catch (error) {
            const errCode = error?.response?.data?.code;
            globalNotification.open({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errCode}.message`),
                description: t(`apiResponses:${errCode}.description`),
            });
            handleResetBack();
        } finally {
            setLoading(prev => ({ ...prev, otp: false }));
        }
    };

    const handleResetBack = () => {
        setResetPasswordStage(passwordResetStages.EMAIL);
        emailForm.resetFields();
        otpForm.resetFields();
        passwordForm.resetFields();
    };
    // #endregion

    const resetPasswordHandlers = {
        handleEmailSubmit,
        handleOtpSubmit,
        handleResetBack,
        setLoading,
    };

    const resetForms = {
        emailForm,
        otpForm,
        passwordForm,
    };

    const sectionStyle = {
        backgroundColor: mobileDevicesBreakpoint ? 'transparent' : primitiveColors.gray0,
        padding: mobileDevicesBreakpoint ? spacing[1] : spacing[4],
    };

    return (
        <PageContainer
            style={{
                backgroundColor: 'transparent',
                padding: 0,
            }}
        >
            <Title className={classes.title}>{t('profile:settings-title')}</Title>
            <ReadOnlyNotice style={{ marginBottom: spacing[6] }} />
            <ConfigProvider componentDisabled={isImpersonating}>
                <Section
                    style={{
                        ...sectionStyle,
                        marginBottom: spacing[6],
                    }}
                >
                    <Flex vertical gap={spacing[5]}>
                        <Title className={classes['sub-title']}>{t('profile:generalTab')}</Title>
                        <ProfileForm
                            form={profileForm}
                            currentUser={currentUser}
                            fetchUser={fetchUser}
                            setLoading={setLoading}
                            loading={loading.profile}
                            t={t}
                        />
                    </Flex>
                </Section>
                <Section
                    style={{
                        ...sectionStyle,
                        marginBottom: spacing[6],
                    }}
                >
                    <Flex vertical gap={spacing[5]}>
                        <Title className={classes['sub-title']}>{t('profile:resetTab')}</Title>
                        {renderResetPasswordContent({
                            stage: resetPasswordStage,
                            forms: resetForms,
                            loading,
                            handlers: resetPasswordHandlers,
                            t,
                            resetEmail: currentUser.email,
                        })}
                    </Flex>
                </Section>
                <Section
                    style={{
                        ...sectionStyle,
                        marginBottom: spacing[6],
                    }}
                >
                    <Flex vertical gap={spacing[5]}>
                        <Title className={classes['sub-title']}>{t('settings:2FA-title')}</Title>
                        <TwoFactorAuth />
                    </Flex>
                </Section>
                <Section
                    id={EMAIL_PREFERENCES_ID}
                    role="region"
                    aria-labelledby={`${EMAIL_PREFERENCES_ID}-title`}
                    style={{
                        ...sectionStyle,
                    }}
                >
                    <Flex vertical gap={spacing[5]}>
                        <Title id={`${EMAIL_PREFERENCES_ID}-title`} className={classes['sub-title']}>
                            {t('settings:email-preferences.title')}
                        </Title>
                        <EmailPreferences disabled={isImpersonating} />
                    </Flex>
                </Section>
            </ConfigProvider>
        </PageContainer>
    );
}
